import { BrokenWaveFileError, WaveMetaInfo, parseWaveMetaInfo } from './WaveMetaInfo';

export class WaveFormatValidator {
  profile: WaveFormatProfile;

  static newDefaultStereoValidator(): WaveFormatValidator {
    return new WaveFormatValidator(WaveFormatProfile.newDefaultStereoProfile());
  }

  constructor(profile: WaveFormatProfile) {
    this.profile = profile;
  }

  async validateFile(file: File) {
    try {
      const waveFormat = await parseWaveMetaInfo(file);
      return this.validateFormat(waveFormat);
    } catch (e) {
      if (e instanceof BrokenWaveFileError) return ['Broken wave file detected. ' + e.message];
      if (e instanceof Error) return ['Error while parsing wave file occurred. ' + e.message];
      return [String(e)];
    }
  }

  validateFormat = (waveFormat: WaveMetaInfo): string[] => {
    const profile = this.profile;
    const validationErrors: string[] = [];
    if (!waveFormat.isValidContainer()) {
      validationErrors.push('Not a valid WAV file. RIFF/RF64 header not found in file.');
      return validationErrors;
    }

    if (waveFormat.fileSize < 176400)
      validationErrors.push(`File must have a size of be at least 176400 Bytes, but is ${waveFormat.fileSize} Bytes`);
    if (waveFormat.format !== 'WAVE') validationErrors.push(`RIFF format is not WAVE but ${waveFormat.format}`);

    if (waveFormat.fmtChunk === null) {
      validationErrors.push('FMT chunk not found in file');
    } else {
      if (profile.allowedCodecs.length > 0 && !profile.allowedCodecs.includes(waveFormat.fmtChunk.audioFormat))
        validationErrors.push(`Invalid codec ${waveFormat.fmtChunk.audioFormat} used in WAV`);

      if (
        (profile.minChannels > 0 && waveFormat.fmtChunk.numChannels < profile.minChannels) ||
        (profile.maxChannels > 0 && waveFormat.fmtChunk.numChannels > profile.maxChannels)
      ) {
        if (profile.minChannels === profile.maxChannels) {
          validationErrors.push(
            `Expecting ${profile.minChannels} channels for file, but has ${waveFormat.fmtChunk.numChannels}`,
          );
        } else {
          validationErrors.push(
            `Expecting ${profile.minChannels}-${profile.maxChannels} channels for file, but has ${waveFormat.fmtChunk.numChannels}`,
          );
        }
      }

      if (waveFormat.fmtChunk.sampleRate < profile.minSampleRate)
        validationErrors.push(
          `Expecting at least ${profile.minSampleRate} Hz Sample Rate for file, but is ${waveFormat.fmtChunk.sampleRate}`,
        );
      if (waveFormat.fmtChunk.bitsPerSample < profile.minBitsPerSample)
        validationErrors.push(
          `Expecting at least ${profile.minBitsPerSample} Bits per Sample for file, but has ${waveFormat.fmtChunk.bitsPerSample}`,
        );
    }

    for (const chunk of profile.forbiddenChunks) {
      if (waveFormat.detectedChunkIDs.includes(chunk)) {
        let chunkName = chunk.toUpperCase();
        if (chunk === WaveFormatProfile.CHUNK__DBMD) chunkName += '(Dolby Atmos Metadata)';
        validationErrors.push(`The WAV-File must not contain a ${chunkName} Chunk`);
      }
    }

    return validationErrors;
  };
}

export class WaveFormatProfile {
  static readonly AUDIO_CODEC__PCM = 1;
  static readonly AUDIO_CODEC__PCM_FLOAT = 3;

  static readonly CHUNK__DBMD = 'dbmd';

  allowedCodecs: number[] = [];
  forbiddenChunks: string[] = [];

  minChannels = 2;
  maxChannels = 2;

  minSampleRate = 44100;
  minBitsPerSample = 16;

  static newDefaultStereoProfile(): WaveFormatProfile {
    const profile = new WaveFormatProfile();
    profile.forbiddenChunks = [WaveFormatProfile.CHUNK__DBMD];
    profile.allowedCodecs = [WaveFormatProfile.AUDIO_CODEC__PCM, WaveFormatProfile.AUDIO_CODEC__PCM_FLOAT];
    return profile;
  }
}
